import { useEffect, useState } from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth';
import { ArtworkUpload } from '../components/admin/ArtworkUpload';
import { StudentManagement } from '../components/admin/StudentManagement';
import { ExhibitionManagement } from '../components/admin/ExhibitionManagement';
import { DashboardOverview } from '../components/admin/DashboardOverview';
import { TagManagement } from '../components/admin/TagManagement';
import ArtworkManagement from '../components/admin/ArtworkManagement';
import { ErrorBoundary } from '../components/ui';

const DashboardContainer = styled.div`
  min-height: calc(100vh - var(--site-header-offset));
  min-height: calc(100dvh - var(--site-header-offset));
  display: grid;
  grid-template-columns: 250px 1fr;
  background-color: ${({ theme }) => theme.colors.background};
  
  /* Safe area padding for mobile */
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    padding-left: env(safe-area-inset-left);
    padding-right: env(safe-area-inset-right);
    padding-bottom: env(safe-area-inset-bottom);
  }
`;

const Sidebar = styled.aside<{ $isOpen: boolean }>`
  background: ${({ theme }) => theme.colors.white};
  border-right: 1px solid ${({ theme }) => theme.colors.gray.light};
  padding: ${({ theme }) => theme.spacing.xl} 0;
  position: fixed;
  top: var(--site-header-offset);
  left: ${({ $isOpen }) => ($isOpen ? '0' : '-250px')};
  width: 250px;
  height: calc(100vh - var(--site-header-offset));
  height: calc(100dvh - var(--site-header-offset));
  z-index: 50;
  transition: left ${({ theme }) => theme.transitions.normal};
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  
  /* Safe area padding on mobile */
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding-left: max(${({ theme }) => theme.spacing.lg}, env(safe-area-inset-left, 0px));
    padding-right: ${({ theme }) => theme.spacing.lg};
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    position: static;
    left: 0;
    height: auto;
    top: auto;
    padding-left: ${({ theme }) => theme.spacing.xl};
  }
`;

const SidebarHeader = styled.div`
  padding: 0 ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray.light};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SidebarTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const SidebarSubtitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.gray.dark};
  margin: 0;
`;

const SidebarNav = styled.nav`
  padding: 0 ${({ theme }) => theme.spacing.md};
`;

const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const NavItem = styled.li`
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const NavLink = styled(Link)<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme, $isActive }) => 
    $isActive ? theme.colors.primary : theme.colors.text};
  background-color: ${({ theme, $isActive }) => 
    $isActive ? theme.colors.primary + '10' : 'transparent'};
  font-weight: ${({ theme, $isActive }) => 
    $isActive ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.normal};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background-color: ${({ theme }) => theme.colors.gray.light};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const NavIcon = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  width: 20px;
  text-align: center;
`;

const MainContent = styled.div<{ $sidebarOpen: boolean }>`
  padding: ${({ theme }) => theme.spacing.xl};
  overflow-y: auto;
  transition: margin-left ${({ theme }) => theme.transitions.normal};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-left: ${({ $sidebarOpen }) => ($sidebarOpen ? '250px' : '0')};
  }
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`;

const PageTitle = styled.h1`
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const MenuToggle = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: block;
  }
`;

const LogoutButton = styled.button`
  background-color: ${({ theme }) => theme.colors.error};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background-color: ${({ theme }) => theme.colors.error}dd;
    transform: translateY(-1px);
  }
`;

const Overlay = styled.div<{ $isVisible: boolean }>`
  display: ${({ $isVisible }) => ($isVisible ? 'block' : 'none')};
  position: fixed;
  top: var(--site-header-offset);
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 40;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

export function AdminDashboard() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Public pages intentionally preserve their browsing position. Entering
  // the fixed-header admin shell starts at its own top-level controls so a
  // scrolled public page cannot leave the dashboard header offscreen.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  const navItems = [
    { path: '/admin', label: 'Overview', icon: '📊', exact: true },
    { path: '/admin/artworks', label: 'Manage Artworks', icon: '🎨' },
    { path: '/admin/upload', label: 'Upload Artwork', icon: '📤' },
    { path: '/admin/students', label: 'Students', icon: '👥' },
    { path: '/admin/tags', label: 'Tags', icon: '🏷️' },
    { path: '/admin/exhibitions', label: 'Exhibitions', icon: '🏛️' },
  ];

  const handleLogout = async () => {
    await logout();
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const isActiveRoute = (path: string, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const getPageTitle = () => {
    const currentPath = location.pathname;
    if (currentPath === '/admin') return 'Dashboard Overview';
    if (currentPath.startsWith('/admin/artworks')) return 'Artwork Management';
    if (currentPath.startsWith('/admin/upload')) return 'Upload Artwork';
    if (currentPath.startsWith('/admin/students')) return 'Student Management';
    if (currentPath.startsWith('/admin/tags')) return 'Tag Management';
    if (currentPath.startsWith('/admin/exhibitions')) return 'Exhibitions';
    return 'Admin Dashboard';
  };

  return (
    <DashboardContainer>
      <Overlay $isVisible={sidebarOpen} onClick={closeSidebar} />
      
      <Sidebar $isOpen={sidebarOpen}>
        <SidebarHeader>
          <SidebarTitle>Teacher Portal</SidebarTitle>
          <SidebarSubtitle>Welcome, {user?.email}</SidebarSubtitle>
        </SidebarHeader>

        <SidebarNav>
          <NavList>
            {navItems.map((item) => (
              <NavItem key={item.path}>
                <NavLink
                  to={item.path}
                  $isActive={isActiveRoute(item.path, item.exact)}
                  onClick={closeSidebar}
                >
                  <NavIcon>{item.icon}</NavIcon>
                  {item.label}
                </NavLink>
              </NavItem>
            ))}
          </NavList>
        </SidebarNav>
      </Sidebar>

      <MainContent $sidebarOpen={sidebarOpen}>
        <Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <MenuToggle onClick={toggleSidebar}>☰</MenuToggle>
            <PageTitle>{getPageTitle()}</PageTitle>
          </div>
          <HeaderActions>
            <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
          </HeaderActions>
        </Header>

        <ErrorBoundary>
          <Routes>
            <Route path="/" element={<DashboardOverview />} />
            <Route path="/artworks" element={<ArtworkManagement />} />
            <Route path="/upload" element={<ArtworkUpload />} />
            <Route path="/students" element={<StudentManagement />} />
            <Route path="/tags" element={<TagManagement />} />
            <Route path="/exhibitions" element={<ExhibitionManagement />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </ErrorBoundary>
      </MainContent>
    </DashboardContainer>
  );
}
